import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Loader2, MoreHorizontal } from 'lucide-react';
import {
  canEditScheduledMessage,
  getScheduledMessageActionHandler,
  getScheduledMessageMenuGroups,
  isScheduledMessageActionLoading,
} from '../scheduledMessageActions';

const menuButtonClass =
  'inline-flex items-center justify-center h-8 w-8 rounded-md text-content-secondary border border-slate-200 bg-white hover:bg-slate-50 hover:text-content-primary transition-colors disabled:opacity-40 disabled:pointer-events-none';

const menuItemClass =
  'block w-full text-left px-3 py-2 text-sm text-content-primary hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:pointer-events-none';

const menuItemDangerClass =
  'block w-full text-left px-3 py-2 text-sm text-red-700 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:pointer-events-none';

const menuSectionLabelClass =
  'px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-content-muted';

function MenuDivider() {
  return <div className="my-1 border-t border-surface-border" role="separator" />;
}

function ScheduledMessageActionsMenu({
  item,
  detailTo,
  editTo,
  loadingState,
  onCancel,
  onDelete,
  onRetry,
  onResend,
  onForceDispatch,
  onSendTemplate,
}) {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const handlers = {
    onCancel,
    onDelete,
    onRetry,
    onResend,
    onForceDispatch,
    onSendTemplate,
  };

  const isBusy = Boolean(loadingState?.activeActionId);
  const canEdit = canEditScheduledMessage(item);
  const menuGroups = getScheduledMessageMenuGroups(item);
  const hasOperationalActions = menuGroups.length > 0;

  const updateMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    const menu = menuRef.current;
    if (!button) {
      return;
    }

    const rect = button.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight ?? 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8;

    setMenuStyle({
      position: 'fixed',
      top: openUpward ? rect.top - menuHeight - 4 : rect.bottom + 4,
      right: Math.max(8, window.innerWidth - rect.right),
      visibility: 'visible',
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }

    updateMenuPosition();
    const frame = requestAnimationFrame(updateMenuPosition);

    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updateMenuPosition, menuGroups.length, canEdit]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      const inButton = buttonRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inButton && !inMenu) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const closeAndRun = (action) => {
    setOpen(false);
    const handler = getScheduledMessageActionHandler(action.id, handlers);
    handler?.(item);
  };

  const menuPanel = open ? (
    <div
      ref={menuRef}
      style={
        menuStyle ?? {
          position: 'fixed',
          top: -9999,
          left: 0,
          visibility: 'hidden',
        }
      }
      className="min-w-[12.5rem] max-w-[16rem] rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
      role="menu"
    >
      <p className={menuSectionLabelClass}>Navegação</p>
      <Link
        to={detailTo}
        onClick={() => setOpen(false)}
        className={menuItemClass}
        role="menuitem"
      >
        Ver detalhes
      </Link>
      {canEdit && (
        <Link
          to={editTo}
          onClick={() => setOpen(false)}
          className={menuItemClass}
          role="menuitem"
        >
          Editar
        </Link>
      )}

      {hasOperationalActions && <MenuDivider />}

      {menuGroups.map((group, groupIndex) => (
        <React.Fragment key={group.id}>
          {group.label && <p className={menuSectionLabelClass}>{group.label}</p>}
          {group.actions.map((action) => {
            const loading = isScheduledMessageActionLoading(action.id, loadingState);
            const itemClass = action.variant === 'danger' ? menuItemDangerClass : menuItemClass;

            return (
              <button
                key={action.id}
                type="button"
                onClick={() => closeAndRun(action)}
                disabled={isBusy}
                className={itemClass}
                role="menuitem"
                title={action.description}
              >
                {loading ? action.loadingLabel : action.label}
              </button>
            );
          })}
          {groupIndex < menuGroups.length - 1 && group.id !== 'danger' && <MenuDivider />}
        </React.Fragment>
      ))}
    </div>
  ) : null;

  return (
    <>
      <div className="inline-flex justify-end">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          disabled={isBusy}
          title="Ações"
          aria-label="Ações do agendamento"
          aria-expanded={open}
          aria-haspopup="menu"
          className={menuButtonClass}
        >
          {isBusy ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <MoreHorizontal className="h-4 w-4 shrink-0" aria-hidden />
          )}
        </button>
      </div>
      {open && createPortal(menuPanel, document.body)}
    </>
  );
}

export default ScheduledMessageActionsMenu;
